// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { ButtonIcon } from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import { useArrowElement } from "@synd/App/Components/XngCarousel/hooks";
import { arrowIconsByDirection, getArrowStyles } from "@synd/App/Components/XngCarousel/styles/arrow.styles";
import { ArrowPosition, ArrowType, NavigationProps } from "@synd/App/Components/XngCarousel/types";
import { xClassNames } from "@synd/XDL/Utilities";
import React, { FC, useCallback, useEffect } from "react";
import { useSwiper } from "swiper/react";

const getArrowPosition = (arrowType: ArrowType, direction: "horizontal" | "vertical" = "horizontal"): ArrowPosition => {
    const isVertical = direction === "vertical";
    if (arrowType === "next") {
        return isVertical ? "bottom" : "right";
    }
    return isVertical ? "top" : "left";
};

const ArrowNavigation: FC<NavigationProps> = React.memo(
    ({
        arrowType,
        showOnMobile,
        buttonIconProps,
        showNavigation,
        swiperInstanceRef,
        showArrowsOutside,
        hideArrowOnDisable,
        ...otherProps
    }) => {
        const swiperInstance = useSwiper() || swiperInstanceRef?.current;
        const arrowPosition = getArrowPosition(arrowType, swiperInstance?.isHorizontal() ? "horizontal" : "vertical");

        const {
            className,
            element: arrowElement,
            disabled,
        } = useArrowElement({
            swiper: swiperInstance,
            showOnMobile,
            arrowType,
            showArrowsOutside,
        });

        const onClickHandler = useCallback(
            (event: any) => {
                arrowElement?.click();
                otherProps.onClick?.(event);
            },
            [arrowElement],
        );

        useEffect(() => {
            otherProps.onDisabled(disabled);
        }, [disabled]);

        if (!swiperInstance || !arrowElement) {
            return null;
        }

        const props = {
            arrowPosition,
            showOnMobile,
            className: xClassNames.append(className, otherProps.className),
        };
        return (
            <Clickable
                {...props}
                {...getArrowStyles({
                    arrowPosition,
                    showOnMobile,
                    hideOnDisabled: !swiperInstance?.isHorizontal() || hideArrowOnDisable,
                })}
                aria-label={`carousel-${arrowPosition}`}
                onClick={onClickHandler}
            >
                <ButtonIcon
                    {...arrowIconsByDirection[arrowPosition]}
                    color="common.white"
                    className={`carousel-${arrowPosition}-icon ${disabled && "disabled"}`}
                    {...buttonIconProps}
                />
            </Clickable>
        );
    },
);

export default ArrowNavigation;
